import axios from "../../axiosClient";

export type TimerProfilePayload = {
  id: number;
  assetCode: string;
  profileName: string;
  startTime: string;
  endTime: string;
  zoneID: number;
  daysOfWeeks: number;
  actionId: number;
  repeatAction: number;
  profileControl?: boolean;
  brightLevel: number;
  dimLevel: number;
};

export type TimerProfile = {
  id: number;
  profileName: string;
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  group: string;
  zoneId: number;
  action: string;
  status: string;
  isAcknowledged: boolean;
  repeatAction: number;
  isBaseProfile: boolean;
  dimLevel: number;
  brightLevel: number;
  profileId: string;
};

export type TimerProfileCommandResult = {
  lastSync: string;
  profiles: {
    zoneId: number | null;
    zoneName?: string;
    schedule: string;
    dimLevel: number | null;
    brightLevel: number | null;
    action: string;
    status: string;
  }[];
};

export const getTimerProfiles = async (
  assetCode: string,
  params?: {
    pageNumber?: number;
    searchText?: string;
    status?: number;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortDirection?: string;
    pageSize?: number;
  },
  signal?: any,
) => {
  const res = await axios.get(
    `/slpapp/TimerProfile/GetTimerProfile`,
    {
      params: { assetCode, ...params },
      signal,
    }
  );
  return res?.data;
};

export const saveTimerProfile = async (payload: any) => {
  const res = await axios.post(
    `/slpapp/TimerProfile/SaveTimerProfile`,
    payload
  );
  return res?.data;
};

export const getTimerProfileCommandResult = async (assetCode: string, signal?: any) => {
  const res = await axios.get(
    `/slpapp/TimerProfile/GetTimerProfileCommandResult`,
    { params: { assetCode }, signal }
  );
  return res?.data;
};

export const sendGetCommandToDms = async (assetCode: string, module: number, signal?: any) => {
  const res = await axios.post(
    `/slpapp/GetCommand/SendGetCommandToDms`,
    { assetCode, module },
    { signal }
  );
  return res?.data;
};

export const getTimerProfileHistory = async (query: any) => {
  const {
    assetCode,
    ackstatus,
    status,
    pageNumber = 1,
    pageSize = 10,
    sortBy = "SentOn",
    sortDirection = "DESC",
    searchText,
  } = query;
  const res = await axios.post(
    `/slpapp/DaylightProfile/GetProfileCommandHistory`,
    {
      pageNumber,
      searchText: searchText || "",
      sortBy,
      sortDirection,
      pageSize,
    },
    {
      params: {
        assetCode,
        profileTypeId: 1,
        status: status ?? ackstatus ?? "All",
      },
    }
  );
  return res?.data;
};

export const getTimerProfileHistoryDetail = async (historyId: number, signal?: any) => {
  const res = await axios.post(
    `/slpapp/DaylightProfile/GetProfileCommandHistoryDetail`,
    undefined,
    { params: { historyId }, signal }
  );
  return res?.data;
};

export const getTimerProfileBackupRestore = async (assetCode: string, signal?: any) => {
  const res = await axios.get(
    `/slpapp/TimerProfile/GetTimerProfileBackupRestore`,
    { params: { assetCode }, signal }
  );
  return res?.data;
};

export const getTimerProfileBaselineResult = async (assetCode: string, signal?: any) => {
  const res = await axios.get(
    `/slpapp/TimerProfile/GetTimerProfileBaselineResult`,
    { params: { assetCode }, signal }
  );
  return res?.data;
};

export const restoreTimerProfile = async (payload: any) => {
  const res = await axios.post(
    `/slpapp/TimerProfile/RestoreTimerProfile`,
    payload
  );
  return res?.data;
};

export const setLightProfileBaseline = async (payload: any[], signal?: any) => {
  const res = await axios.post(
    `/slpapp/TimerProfile/SetLightProfileBaseline`,
    payload,
    { signal }
  );
  return res?.data;
};

export const applyBaselineToTimerProfile = async (payload: any[], signal?: any) => {
  const res = await axios.post(
    `/slpapp/TimerProfile/ApplyBaselineToTimerProfile`,
    payload,
    { signal }
  );
  return res?.data;
};

export const getZoneInfo = async (assetCode: string, zoneId: number, signal?: any) => {
  const res = await axios.get(
    `/slpapp/TimerProfile/GetZoneInfo`,
    { params: { assetCode, zoneId }, signal }
  );
  return res?.data;
};

export const getTimerProfileById = async (timerProfileId: number, signal?: any) => {
  const res = await axios.get(
    `/slpapp/TimerProfile/GetTimerProfileById`,
    { params: { timerProfileId }, signal }
  );
  return res?.data;
};

export const getDaysOfWeek = async (signal?: any) => {
  const res = await axios.get(`/slpapp/DaysOfWeek`, { signal });
  return res?.data;
};

export const getOperationModes = async (signal?: any) => {
  const res = await axios.get(`/slpapp/OperationMode/OperationMode`, { signal });
  return res?.data;
};

export const updateTimerProfileStatus = async (
  nums: number[],
  isEnable: boolean,
  repeatAction = 0,
  signal?: any,
) => {
  const res = await axios.post(
    `/slpapp/TimerProfile/UpdateTimerProfile?isEnable=${String(isEnable)}&repeatAction=${repeatAction}`,
    { nums },
    { signal },
  );
  return res?.data;
};

export const setTimerProfile = async (
  payload: TimerProfilePayload,
  signal?: any,
) => {
  const res = await axios.post(`/slpapp/TimerProfile/SetTimerProfile`, payload, {
    signal,
  });
  return res?.data;
};

export const updateTimerProfile = async (
  payload: TimerProfilePayload,
  signal?: any,
) => {
  const res = await axios.put(`/slpapp/TimerProfile`, payload, { signal });
  return res?.data;
};

export const deleteTimerProfile = async (
  timerProfileId: number,
  signal?: any,
) => {
  const res = await axios.delete(`/slpapp/TimerProfile`, {
    params: { timerProfileId },
    signal,
  });
  return res?.data;
};


export const getGatewayZone = async (
  gatewayCode: string,
  PageNumber: any = 0,
  pageSize: any = 0,
) => {
  if (!gatewayCode) return;
  const res = await axios.get(`/slpapp/GatewayZone`, {
    params: { gatewayCode, PageNumber, pageSize },
  });

  return res?.data;
};